import { Configuration } from "@azure/msal-browser";
// Browser check variables
// If you support IE, our recommendation is that you sign-in using Redirect APIs
// If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
const ua = window.navigator.userAgent;
const msie = ua.indexOf("MSIE ");
const msie11 = ua.indexOf("Trident/");
const msedge = ua.indexOf("Edge/");
const firefox = ua.indexOf("Firefox");
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;
// Only needed if you need to support the redirect flow in Firefox incognito
const isFirefox = firefox > 0;

interface DashboardConfiguration {
  debug: boolean;
  msalConfig: Configuration;
  rootAPIUrl: string | undefined;
  rootFunctionCode: string;
  rootAPIList: RootAPIList | undefined;
  applicationName: string;
}

interface RootAPIList {
  events: string;
  event: string;
  sendForApproval: string;
  sendForRejection: string;
  approved: string;
  archive: string;
  complete: string;
  generateReport: string;
  savedReportTotal: string;
  auditTrail: string;
  invoiceInfo: string;
  accountingApproval: string;
  eventSearch: string;
  vendorSearch: string;
  eventCreate: string;
  eventCreationPolicy: string;
  cancel: string;
  locations: string;
  createTicket: string;
  reports: string;
  generateInvoice: string;
  invoiceStatus: string;
  rejectContribution: string;
  contributionUpdate: string;
  getMessages: string;
  createMessage: string;
  scopes: string[];
  allUserPermissions: string;
  saveEventDate: string;
}

export let config: DashboardConfiguration = {
  debug: false,
  // Config object to be passed to Msal on creation
  msalConfig: {
    auth: {
      clientId: "",
      authority: "",
      redirectUri: "", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
      postLogoutRedirectUri: "", // Indicates the page to navigate after logout.
      clientCapabilities: ["CP1"], // this lets the resource owner know that this client is capable of handling claims challenge.
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: isIE || isEdge || isFirefox,
    },
    system: {
      // allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
        },
      },
    },
  },
  rootAPIUrl: undefined,
  rootFunctionCode: ``,
  rootAPIList: undefined,
  applicationName: `SpiritNight`,
};

// TODO: Update those once static apps are created
let devUrl: string = "kind-rock-00e1b490f.5.azurestaticapps.net";
let devDomain: string = "spiritnight-test.torchys.com";
let prodDomain: string = "spiritnight.torchys.com";
let prodUrl: string = "jolly-coast-079df0b0f.5.azurestaticapps.net";
let releaseUrl: string = "nice-beach-011e6a40f.5.azurestaticapps.net";

if (window.location.hostname === prodUrl || window.location.hostname === prodDomain) {
  config.rootAPIUrl = `https://func-spirit-night-prod.azurewebsites.net`;
  config.rootFunctionCode = `NsnaK-1h_BhzMENup1cqgYVagfnhauOVdOolZ_fErEL2AzFujRy86Q==`;
} else if (window.location.hostname === releaseUrl) {
  config.rootAPIUrl = `https://func-spirit-night-release.azurewebsites.net`;
  config.rootFunctionCode = `BCBYYO4Mwr6DlahgyQHyXl612T2go9F989IMb19ZZVozAzFuFrYIKg==`;
} else if (window.location.hostname === devUrl || window.location.hostname === devDomain) {
  config.rootAPIUrl = `https://func-spirit-night-tst-v2.azurewebsites.net`;
  config.rootFunctionCode = `MmgY1GAP21sKe4BdskfnLPgSOvju2iuUTbP6qJ7pWwVDAzFu_r2KgQ==`;
}

if (window.location.hostname === "localhost") {
  //Uncomment for dev
  // config.rootAPIUrl = `https://func-spirit-night-tst-v2.azurewebsites.net`;
  // config.rootFunctionCode = `MmgY1GAP21sKe4BdskfnLPgSOvju2iuUTbP6qJ7pWwVDAzFu_r2KgQ==`;
}

if (window.location.hostname === prodUrl || window.location.hostname === prodDomain) {
  config.rootAPIList = {
    accountingApproval: `/api/event/accounting-approval?eventId={id}&code=${config.rootFunctionCode}`,
    approved: `/api/event/approved?code=${config.rootFunctionCode}`,
    archive: `/api/event/archive?code=${config.rootFunctionCode}`,
    cancel: `/api/event/cancel?eventId={id}&reason={reason}&code=${config.rootFunctionCode}`,
    complete: `/api/event/complete?eventId={id}&salesContribution={salesContribution}&donationContribution={donationContribution}&code=${config.rootFunctionCode}`,
    contributionUpdate: `/api/event/contributionUpdate?eventId={id}&addlSales={addlSales}&addlDonation={addlDonation}&code=${config.rootFunctionCode}`,
    eventCreate: `/api/event/create?code=${config.rootFunctionCode}`,
    eventCreationPolicy: `/api/event/creation-policy?code=${config.rootFunctionCode}`,
    saveEventDate: `/api/event/update-date?code=${config.rootFunctionCode}`,
    createTicket: `/api/createTicket?code=${config.rootFunctionCode}`,
    sendForRejection: `/api/event/send-for-rejection?code=${config.rootFunctionCode}`,
    event: `/api/event?id={id}&code=${config.rootFunctionCode}`,
    eventSearch: `/api/vendor/search?code=${config.rootFunctionCode}`,
    events: `/api/events?oldEvents={oldEvents}&archived={archived}&code=${config.rootFunctionCode}`,
    vendorSearch: `/api/vendor/search?VendorName={name}&code=${config.rootFunctionCode}`,
    generateInvoice: `/api/event/generate-invoice?code=${config.rootFunctionCode}`,
    generateReport: `/api/generate-report?eventId={id}&code=${config.rootFunctionCode}`,
    invoiceInfo: `/api/event/invoice-info?eventId={id}&code=${config.rootFunctionCode}`,
    locations: `/api/locations?code=${config.rootFunctionCode}`,
    invoiceStatus: `/api/event/invoiced/status?eventId={id}&code=${config.rootFunctionCode}`,
    reports: `/api/report?date={date}&code=${config.rootFunctionCode}`,
    savedReportTotal: `/api/saved-result-report?eventId={id}&code=${config.rootFunctionCode}`,
    auditTrail: `/api/event/audit-trail?eventId={id}&code=${config.rootFunctionCode}`,
    sendForApproval: `/api/event/send-for-approval?code=${config.rootFunctionCode}`,
    rejectContribution: `/api/event/rejectContribution?eventId={id}&code=${config.rootFunctionCode}`,
    getMessages: `/api/event/messages?eventId={id}&code=${config.rootFunctionCode}`,
    createMessage: `/api/event/createMessage?code=${config.rootFunctionCode}`,
    scopes: ["api://d009ab97-08c8-4a7f-8662-b83bba5c5e63/Roles", "User.Read"],
    allUserPermissions: `/api/authorization/applications/{applicationName}/users/{email}/allpermissions?code=${config.rootFunctionCode}`,
    //prod
  };
} else if (window.location.hostname === releaseUrl) {
  config.rootAPIList = {
    events: `/api/events?oldEvents={oldEvents}&archived={archived}&code=${config.rootFunctionCode}`,
    event: `/api/event?id={id}&code=${config.rootFunctionCode}`,
    archive: `/api/event/archive?code=${config.rootFunctionCode}`,
    sendForApproval: `/api/event/send-for-approval?code=${config.rootFunctionCode}`,
    sendForRejection: `/api/event/send-for-rejection?code=${config.rootFunctionCode}`,
    eventSearch: `/api/vendor/search?code=${config.rootFunctionCode}`,
    eventCreate: `/api/event/create?code=${config.rootFunctionCode}`,
    eventCreationPolicy: `/api/event/creation-policy?code=${config.rootFunctionCode}`,
    saveEventDate: `/api/event/update-date?code=${config.rootFunctionCode}`,
    vendorSearch: `/api/vendor/search?VendorName={name}&code=${config.rootFunctionCode}`,
    locations: `/api/locations?code=${config.rootFunctionCode}`,
    approved: `/api/event/approved?code=${config.rootFunctionCode}`,
    complete: `/api/event/complete?eventId={id}&salesContribution={salesContribution}&donationContribution={donationContribution}&code=${config.rootFunctionCode}`,
    cancel: `/api/event/cancel?eventId={id}&reason={reason}&code=${config.rootFunctionCode}`,
    generateReport: `/api/generate-report?eventId={id}&code=${config.rootFunctionCode}`,
    accountingApproval: `/api/event/accounting-approval?eventId={id}&code=${config.rootFunctionCode}`,
    savedReportTotal: `/api/saved-result-report?eventId={id}&code=${config.rootFunctionCode}`,
    auditTrail: `/api/event/audit-trail?eventId={id}&code=${config.rootFunctionCode}`,
    createTicket: `/api/createTicket?code=${config.rootFunctionCode}`,
    reports: `/api/report?date={date}&code=${config.rootFunctionCode}`,
    generateInvoice: `/api/event/generate-invoice?&code=${config.rootFunctionCode}`,
    invoiceInfo: `/api/event/invoice-info?eventId={id}&code=${config.rootFunctionCode}`,
    invoiceStatus: `/api/event/invoiced/status?eventId={id}&code=${config.rootFunctionCode}`,
    rejectContribution: `/api/event/rejectContribution?eventId={id}&code=${config.rootFunctionCode}`,
    contributionUpdate: `/api/event/contributionUpdate?eventId={id}&addlSales={addlSales}&addlDonation={addlDonation}&code=${config.rootFunctionCode}`,
    getMessages: `/api/event/messages?eventId={id}&code=${config.rootFunctionCode}`,
    createMessage: `/api/event/createMessage?code=${config.rootFunctionCode}`,
    scopes: ["api://d009ab97-08c8-4a7f-8662-b83bba5c5e63/Roles", "User.Read"],
    allUserPermissions: `/api/authorization/applications/{applicationName}/users/{email}/allpermissions?code=${config.rootFunctionCode}`,
    //release
  };
} else if (window.location.hostname === devUrl || window.location.hostname === devDomain) {
  config.rootAPIList = {
    accountingApproval: `/api/event/accounting-approval?eventId={id}&code=${config.rootFunctionCode}`,
    approved: `/api/event/approved?code=${config.rootFunctionCode}`,
    archive: `/api/event/archive?code=${config.rootFunctionCode}`,
    cancel: `/api/event/cancel?eventId={id}&reason={reason}&code=${config.rootFunctionCode}`,
    complete: `/api/event/complete?eventId={id}&salesContribution={salesContribution}&donationContribution={donationContribution}&code=${config.rootFunctionCode}`,
    contributionUpdate: `/api/event/contributionUpdate?eventId={id}&addlSales={addlSales}&addlDonation={addlDonation}&code=${config.rootFunctionCode}`,
    eventCreate: `/api/event/create?code=${config.rootFunctionCode}`,
    eventCreationPolicy: `/api/event/creation-policy?code=${config.rootFunctionCode}`,
    saveEventDate: `/api/event/update-date?code=${config.rootFunctionCode}`,
    createTicket: `/api/createTicket?code=${config.rootFunctionCode}`,
    sendForRejection: `/api/event/send-for-rejection?code=${config.rootFunctionCode}`,
    event: `/api/event?id={id}&code=${config.rootFunctionCode}`,
    eventSearch: `/api/vendor/search?code=${config.rootFunctionCode}`,
    events: `/api/events?oldEvents={oldEvents}&archived={archived}&code=${config.rootFunctionCode}`,
    vendorSearch: `/api/vendor/search?VendorName={name}&code=${config.rootFunctionCode}`,
    generateInvoice: `/api/event/generate-invoice?code=${config.rootFunctionCode}`,
    generateReport: `/api/generate-report?eventId={id}&code=${config.rootFunctionCode}`,
    invoiceInfo: `/api/event/invoice-info?eventId={id}&code=${config.rootFunctionCode}`,
    locations: `/api/locations?code=${config.rootFunctionCode}`,
    invoiceStatus: `/api/event/invoiced/status?eventId={id}&code=${config.rootFunctionCode}`,
    reports: `/api/report?date={date}&code=${config.rootFunctionCode}`,
    savedReportTotal: `/api/saved-result-report?eventId={id}&code=${config.rootFunctionCode}`,
    auditTrail: `/api/event/audit-trail?eventId={id}&code=${config.rootFunctionCode}`,
    sendForApproval: `/api/event/send-for-approval?code=${config.rootFunctionCode}`,
    rejectContribution: `/api/event/rejectContribution?eventId={id}&code=${config.rootFunctionCode}`,
    getMessages: `/api/event/messages?eventId={id}&code=${config.rootFunctionCode}`,
    createMessage: `/api/event/createMessage?code=${config.rootFunctionCode}`,
    scopes: ["api://d009ab97-08c8-4a7f-8662-b83bba5c5e63/Roles", "User.Read"],
    allUserPermissions: `/api/authorization/applications/{applicationName}/users/{email}/allpermissions?code=${config.rootFunctionCode}`,

    //dev
  };
} else {
  config.rootAPIUrl = `http://localhost:7231`;
  config.rootAPIList = {
    events: `/api/events?oldEvents={oldEvents}&archived={archived}`,
    event: `/api/event?id={id}`,
    sendForApproval: `/api/event/send-for-approval`,
    sendForRejection: `/api/event/send-for-rejection`,
    archive: `/api/event/archive`,
    eventSearch: `/api/vendor/search`,
    eventCreate: `/api/event/create`,
    eventCreationPolicy: `/api/event/creation-policy`,
    saveEventDate: `/api/event/update-date?code=${config.rootFunctionCode}`,
    vendorSearch: `/api/vendor/search`,
    locations: `/api/locations`,
    approved: `/api/event/approved`,
    complete: `/api/event/complete?eventId={id}&salesContribution={salesContribution}&donationContribution={donationContribution}`,
    cancel: `/api/event/cancel?eventId={id}&reason={reason}`,
    generateReport: `/api/generate-report?eventId={id}`,
    accountingApproval: `/api/event/accounting-approval?eventId={id}`,
    savedReportTotal: `/api/saved-result-report?eventId={id}`,
    auditTrail: `/api/event/audit-trail?eventId={id}`,
    createTicket: `/api/createTicket`,
    reports: `/api/report?date={date}`,
    generateInvoice: `/api/event/generate-invoice`,
    invoiceInfo: `/api/event/invoice-info?eventId={id}`,
    invoiceStatus: `/api/event/invoiced/status?eventId={id}`,
    rejectContribution: `/api/event/rejectContribution?eventId={id}`,
    contributionUpdate: `/api/event/contributionUpdate?eventId={id}&addlSales={addlSales}&addlDonation={addlDonation}`,
    getMessages: `/api/event/messages?eventId={id}`,
    createMessage: `/api/event/createMessage`,
    scopes: [`api://d009ab97-08c8-4a7f-8662-b83bba5c5e63/Roles`, `User.Read`], // backends api scope.
    allUserPermissions: `/api/authorization/applications/{applicationName}/users/{email}/allpermissions`,
    //local
  };
  //********************************************* */
  //  config.rootAPIUrl = "https://func-spirit-night-tst-v2.azurewebsites.net";

  //  };
  //********************************************* */
}

// TODO: Update those once real app registration is done.
config.msalConfig.auth = {
  // This should be the Client ID of the front-end app registration
  clientId: `3e3a49bf-3d72-4dfb-8ea2-c8219ce16f71`,
  authority:
    // https://login.microsoftonline.com/{Tenant ID}
    `https://login.microsoftonline.com/d399117b-c79f-4544-82d0-ab17b7a15628/`,
  // This lets the resource owner know that this client is capable of handling claims challenge.
  clientCapabilities: [`CP1`],
};
console.log(window.location.hostname);
if (
  window.location.hostname === prodUrl ||
  window.location.hostname === prodDomain ||
  window.location.hostname === devUrl ||
  window.location.hostname === devDomain ||
  window.location.hostname === releaseUrl
) {
  config.msalConfig.auth.redirectUri = `${window.location.protocol}//${window.location.hostname}`; // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
  config.msalConfig.auth.postLogoutRedirectUri = `${window.location.protocol}//${window.location.hostname}`; // Indicates the page to navigate after logout.
} else {
  //localhost
  config.msalConfig.auth.redirectUri = `${window.location.protocol}//${window.location.hostname}:3000`; // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
  config.msalConfig.auth.postLogoutRedirectUri = `${window.location.protocol}//${window.location.hostname}:3000`; // Indicates the page to navigate after logout.
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: { scopes: string[]; resource: string } = {
  scopes: [...config.rootAPIList.scopes],
  resource: `https://management.core.windows.net/`,
};

export const appRoles = {
  Admin: `Admin`,
};

export const pageNames = {
  events: `Events`,
  eventJourney: `Journey`,
  reports: `Reports`,
  eventCreate: `Create Event`,
  helpPage: `Help Page`,
};
